import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SILENT_ERROR_CONTEXT } from '@app/interceptors/http.interceptor';
import { ErrorService } from '@app/services/error.service';
import { Cell } from '@data/models/cell.model';
import { Embedded } from '@data/models/embed.model';
import { embedGdpr, EmbedGdpr, Gdpr, gdprConfiguration } from '@data/models/gdpr.dto';
import { GdprRaw } from '@data/models/gdpr.model';
import { SAAS_TOOLS } from '@modules/projects/gdpr.data';
import { toParams } from '@shared/utils/query-to-params';
import { uniq, isEmpty, transform, unset } from 'lodash';
import { Observable, lastValueFrom } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

export const GDPR_SERVICE_API_ROOT = '/gdpr/v1/projects';

@Injectable()
export class GdprService {

    private errors;

    constructor(
        private errorService: ErrorService,
        private httpClient: HttpClient
    ) {
        this.errors = this.errorService.gdprServiceErrors;
    }

    public getSaasDescriptionByName(name: string): string {
        return SAAS_TOOLS[name.toUpperCase()]?.description || '';
    }

    public getGdpr(projectId: string, options?: { silent?: boolean }): Observable<Gdpr> {
        return this.httpClient.get<GdprRaw>(`${GDPR_SERVICE_API_ROOT}/${projectId}/gdpr`, {
            context: new HttpContext().set(SILENT_ERROR_CONTEXT, true)
        })
            .pipe(
                map((gdpr) => Gdpr.fromRaw(gdpr)),
                catchError(options?.silent ? err => new Promise<any>((resolve) => {
                    resolve(err);
                }) : this.errorService.errorHandler<Gdpr>(this.errors, 'Récupération des données RGPD', null))
            );
    }

    public getGdprWithEmbed<T extends embedGdpr>(projectId: string, embedFields: T[] = []): Observable<EmbedGdpr<T>> {
        const embeds = uniq(embedFields.map((it) => gdprConfiguration[it].embed)).join(',');
        const params = toParams({ embed: embeds });

        return this.httpClient
            .get<Gdpr & { embedded: Embedded }>(`${GDPR_SERVICE_API_ROOT}/${projectId}/gdpr`, { params })
            .pipe(
                map(({ embedded, ...gdpr }) => Gdpr.toCustomGdpr(new Gdpr(gdpr), embedded, embedFields)),
                catchError(this.errorService.errorHandler<EmbedGdpr<T>>(this.errors, 'Récupération des données RGPD', null))
            );
    }

    public updateGdpr(gdpr: GdprRaw, options?: { fieldMask?: string[]; shouldThrowError?: boolean }): Observable<Gdpr> {
        const project = gdpr.project;
        if (!isEmpty(options?.fieldMask)) {
            gdpr = transform(options.fieldMask, (accumulator, key) => {
                accumulator[key] = gdpr[key];
            }, {});
        }

        // remove tags property setted localy to facilitate cell update
        unset(gdpr, 'tags');

        return this.httpClient.patch<GdprRaw>(`${GDPR_SERVICE_API_ROOT}/${project}/gdpr`, gdpr).pipe(
            map((response) => Gdpr.fromRaw(response)),
            catchError(this.errorService.errorHandler<Gdpr>(this.errors, 'Modification de données RGPD', null, options?.shouldThrowError))
        );
    }

    public async getProjectGdpr<T extends embedGdpr>(projectId: string, cell: Cell, embedFields = []): Promise<EmbedGdpr<T>> {
        const gdpr = await lastValueFrom(this.getGdprWithEmbed(projectId, embedFields));
        if (!gdpr) {
            return;
        }

        gdpr.tags = cell?.tags;

        return gdpr;
    }

}
